import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { f7, f7ready } from 'framework7-react';

/**
 * Wire the Android hardware / gesture back button to Framework7's router.
 *
 * F7 runs with in-memory routing on mobile (no `browserHistory`/pushState - see
 * App.tsx), so the WebView's own history stack stays empty as the user navigates
 * F7 pages. Capacitor's default back handling only consults that WebView history,
 * so without this the back button never pops the F7 page stack - it just exits
 * the app on every screen. We take over `backButton` and drive F7 ourselves.
 *
 * Priority (topmost UI first):
 *   1. Close an open overlay (sheet / popup / popover / actions / dialog / panel).
 *   2. Pop the current View's router stack when there's a page to go back to.
 *   3. Nothing left to pop - minimize the app (native "home", not a hard exit).
 *
 * Android only; a no-op elsewhere. Idempotent; safe to call once at startup.
 */
let installed = false;

export function installAndroidBackButton(): void {
  if (installed || Capacitor.getPlatform() !== 'android') return;
  installed = true;

  f7ready(() => {
    CapApp.addListener('backButton', () => {
      // 1. Any open overlay swallows the press by closing itself. `.modal-in` is
      // F7's "currently open" marker for every modal type; a panel uses its own.
      if (document.querySelector('.modal-in')) {
        f7.sheet.close();
        f7.popup.close();
        f7.popover.close();
        f7.actions.close();
        f7.dialog.close();
        return;
      }
      if (document.querySelector('.panel.panel-in')) {
        f7.panel.close();
        return;
      }

      // 2. Pop the active View's stack when a previous page exists.
      const router = f7.views.current?.router;
      if (router && router.history.length > 1) {
        router.back();
        return;
      }

      // 3. Root of the stack - background the app instead of navigating.
      void CapApp.minimizeApp();
    });
  });
}
